import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { 
  RefreshCw, 
  Sliders, 
  MessageCircle, 
  Send, 
  X, 
  Sparkles,
  ChevronRight,
  Loader2,
  Bot,
  CheckCircle2,
  ArrowRight,
  Lightbulb,
  Target
} from 'lucide-react';

// Format refined result as readable text instead of raw JSON
const formatRefinedResult = (result) => {
  if (!result) return '';
  
  const labels = {
    brand_colors: '🎨 Couleurs de marque',
    visual_aesthetic: '✨ Esthétique visuelle',
    brand_tone: '💬 Ton de marque',
    typography: '🔤 Typographie',
    imagery_style: '📸 Style d\'imagerie',
    who_she_is_becoming: '👑 Qui tu deviens',
    positioning_direction: '🧭 Direction de positionnement',
    signature: '✍️ Ta signature',
    artistic_direction: '🎭 Direction artistique'
  };
  
  let formatted = '✅ Voici tes modifications appliquées :\n\n';
  
  for (const [key, value] of Object.entries(result)) {
    if (value && typeof value === 'string') {
      const label = labels[key] || key.replace(/_/g, ' ');
      formatted += `${label}\n${value}\n\n`;
    }
  }
  
  return formatted.trim();
};

// Get step completion summary based on tool type
const getStepCompletionSummary = (toolType, result, t) => {
  const summaries = {
    vision_identity: {
      title: t('stepCompletion.visionIdentity.title', 'Identité de Vision créée'),
      summary: t('stepCompletion.visionIdentity.summary', 'Tu viens de définir les fondations visuelles de ta marque.'),
      insight: t('stepCompletion.visionIdentity.insight', 'Cette identité reflète qui tu es vraiment. Elle guidera toutes tes décisions visuelles.'),
      actions: [
        t('stepCompletion.visionIdentity.action1', 'Sauvegarde tes couleurs dans ton téléphone'),
        t('stepCompletion.visionIdentity.action2', 'Commence à appliquer ces couleurs dans tes visuels'),
        t('stepCompletion.visionIdentity.action3', 'Partage cette direction avec ton équipe')
      ]
    },
    brand_audit: {
      title: t('stepCompletion.brandAudit.title', 'Audit de marque terminé'),
      summary: t('stepCompletion.brandAudit.summary', 'Tu as analysé ton positionnement actuel.'),
      insight: t('stepCompletion.brandAudit.insight', 'Cette analyse te donne une base solide pour construire ta stratégie.'),
      actions: [
        t('stepCompletion.brandAudit.action1', 'Note les points forts à amplifier'),
        t('stepCompletion.brandAudit.action2', 'Identifie un axe d\'amélioration prioritaire'),
        t('stepCompletion.brandAudit.action3', 'Planifie une action concrète cette semaine')
      ]
    },
    content_ideas: {
      title: t('stepCompletion.contentIdeas.title', 'Idées de contenu générées'),
      summary: t('stepCompletion.contentIdeas.summary', 'Tu as des idées fraîches pour ton contenu.'),
      insight: t('stepCompletion.contentIdeas.insight', 'Ces idées sont alignées avec ta vision. Elles attireront les bonnes personnes.'),
      actions: [
        t('stepCompletion.contentIdeas.action1', 'Choisis une idée à créer cette semaine'),
        t('stepCompletion.contentIdeas.action2', 'Ajoute-les à ton calendrier de contenu'),
        t('stepCompletion.contentIdeas.action3', 'Commence par l\'idée qui te parle le plus')
      ]
    },
    default: {
      title: t('stepCompletion.default.title', 'Étape complétée'),
      summary: t('stepCompletion.default.summary', 'Tu viens de franchir une étape importante dans ton parcours.'),
      insight: t('stepCompletion.default.insight', 'Chaque action te rapproche de la marque que tu es en train de construire.'),
      actions: [
        t('stepCompletion.default.action1', 'Prends un moment pour célébrer cette avancée'),
        t('stepCompletion.default.action2', 'Note ce que tu as appris'),
        t('stepCompletion.default.action3', 'Continue sur ta lancée')
      ]
    }
  };

  return summaries[toolType] || summaries.default;
};

const AIRefinementPanel = ({ 
  isOpen, 
  onClose, 
  toolType, 
  currentResult, 
  onResultUpdate,
  suggestedPrompts = []
}) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeMode, setActiveMode] = useState(null); // regenerate, adjust, question
  const [userMessage, setUserMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [nextStep, setNextStep] = useState(null);
  const [loadingNextStep, setLoadingNextStep] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  // Fetch next step when completion screen shows
  const fetchNextStep = async () => {
    setLoadingNextStep(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/journey/main-progress`, { headers });
      if (response.data.next_step) {
        setNextStep(response.data.next_step);
      }
    } catch (error) {
      console.error('Error fetching next step:', error);
    } finally {
      setLoadingNextStep(false);
    }
  };

  // Handle continue to next step
  const handleContinueJourney = () => {
    if (nextStep?.route) {
      onClose();
      navigate(nextStep.route);
    } else {
      onClose();
      navigate('/dashboard');
    }
  };

  // Reset completion screen when panel closes
  useEffect(() => {
    if (!isOpen) {
      setShowCompletionScreen(false);
      setNextStep(null);
    }
  }, [isOpen]);

  const defaultPrompts = {
    regenerate: [
      t('aiCoach.quickPrompts.regenerate.different'),
      t('aiCoach.quickPrompts.regenerate.luxury'),
      t('aiCoach.quickPrompts.regenerate.bold'),
      t('aiCoach.quickPrompts.regenerate.soft')
    ],
    adjust: [
      t('aiCoach.quickPrompts.adjust.warmer'),
      t('aiCoach.quickPrompts.adjust.elegant'),
      t('aiCoach.quickPrompts.adjust.simplify'),
      t('aiCoach.quickPrompts.adjust.actionOriented')
    ],
    question: [
      t('aiCoach.quickPrompts.question.whyColors'),
      t('aiCoach.quickPrompts.question.howImplement'),
      t('aiCoach.quickPrompts.question.whatPriority'),
      t('aiCoach.quickPrompts.question.explainMore')
    ]
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!userMessage.trim() || !activeMode) return;

    const newUserMessage = { role: 'user', content: userMessage };
    setConversation(prev => [...prev, newUserMessage]);
    setLoading(true);
    setUserMessage('');

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/ai/refine`, {
        tool_type: toolType,
        original_result: currentResult,
        refinement_type: activeMode,
        user_message: userMessage,
        conversation_history: conversation
      }, { headers });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response || formatRefinedResult(response.data.refined_result),
        type: response.data.type
      };
      
      setConversation(prev => [...prev, assistantMessage]);

      // If we got a refined result, update the parent and show completion screen
      if (response.data.type === 'result' && response.data.refined_result) {
        onResultUpdate(response.data.refined_result);
        toast.success(t('aiCoach.resultsUpdated'));
        
        // Show completion screen after successful application
        setTimeout(() => {
          setShowCompletionScreen(true);
          fetchNextStep();
        }, 1500);
      }
    } catch (error) {
      toast.error(t('aiCoach.errorProcessing'));
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: t('aiCoach.errorMessage'),
        type: 'text'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    setUserMessage(prompt);
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'regenerate': return <RefreshCw size={16} />;
      case 'adjust': return <Sliders size={16} />;
      case 'question': return <MessageCircle size={16} />;
      default: return null;
    }
  };

  const getModeLabel = (mode) => {
    switch (mode) {
      case 'regenerate': return t('aiCoach.regenerate');
      case 'adjust': return t('aiCoach.adjust');
      case 'question': return t('aiCoach.askAi');
      default: return '';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: '100%', opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0A0A0A] border-l border-[#1C1C1C] z-[60] flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="p-4 border-b border-[#1C1C1C] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37]/20 to-[#E5C585]/10 flex items-center justify-center">
              <Bot className="text-[#D4AF37]" size={20} />
            </div>
            <div>
              <h3 className="font-serif text-white">{t('aiCoach.title')}</h3>
              <p className="text-[#52525B] text-xs">{t('aiCoach.subtitle')}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-[#52525B] hover:text-white transition-colors p-2"
            data-testid="close-refinement-panel"
          >
            <X size={20} />
          </button>
        </div>

        {/* Mode Selection - Hidden when completion screen is shown */}
        {!showCompletionScreen && (
          <div className="p-4 border-b border-[#1C1C1C]">
            <p className="text-[#A1A1AA] text-xs mb-3 font-accent tracking-wider">{t('aiCoach.whatToDo')}</p>
            <div className="flex gap-2">
              {['regenerate', 'adjust', 'question'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => {
                    setActiveMode(mode);
                    setConversation([]);
                  }}
                  className={`flex-1 py-3 px-3 text-xs font-medium transition-all flex items-center justify-center gap-2
                    ${activeMode === mode 
                      ? 'bg-[#D4AF37] text-black' 
                      : 'border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/50 hover:text-white'
                    }`}
                  data-testid={`mode-${mode}`}
                >
                  {getModeIcon(mode)}
                  {getModeLabel(mode)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step Completion Screen */}
        {showCompletionScreen ? (
          <div className="flex-1 overflow-y-auto p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* Success Header */}
              <div className="text-center pb-6 border-b border-[#1C1C1C]">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-16 h-16 bg-gradient-to-br from-[#D4AF37]/20 to-[#E5C585]/10 flex items-center justify-center mx-auto mb-4"
                >
                  <CheckCircle2 className="text-[#D4AF37]" size={32} />
                </motion.div>
                <h2 className="font-serif text-2xl text-white mb-2">
                  {getStepCompletionSummary(toolType, currentResult, t).title}
                </h2>
                <p className="text-[#A1A1AA] text-sm">
                  {getStepCompletionSummary(toolType, currentResult, t).summary}
                </p>
              </div>

              {/* Insight Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-[#1C1C1C]/50 p-5 border-l-2 border-[#D4AF37]"
              >
                <div className="flex items-start gap-3">
                  <Lightbulb className="text-[#D4AF37] flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-[#D4AF37] text-xs font-accent tracking-wider mb-2">
                      {t('stepCompletion.insightTitle', 'CE QUE ÇA RÉVÈLE')}
                    </p>
                    <p className="text-[#E4E4E7] text-sm leading-relaxed">
                      {getStepCompletionSummary(toolType, currentResult, t).insight}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Actions Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Target className="text-[#E5C585]" size={18} />
                  <p className="text-[#E5C585] text-xs font-accent tracking-wider">
                    {t('stepCompletion.actionsTitle', 'TES ACTIONS CONCRÈTES')}
                  </p>
                </div>
                <div className="space-y-3">
                  {getStepCompletionSummary(toolType, currentResult, t).actions.map((action, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      className="flex items-start gap-3 p-3 bg-[#0A0A0A] border border-[#27272A]"
                    >
                      <span className="w-6 h-6 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-medium flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <p className="text-[#E4E4E7] text-sm">{action}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Next Step Preview */}
              {nextStep && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="bg-gradient-to-br from-[#D4AF37]/5 to-transparent p-5 border border-[#D4AF37]/20"
                >
                  <p className="text-[#52525B] text-xs font-accent tracking-wider mb-2">
                    {t('stepCompletion.nextStep', 'PROCHAINE ÉTAPE')}
                  </p>
                  <p className="text-white font-serif text-lg mb-1">{nextStep.name}</p>
                  <p className="text-[#A1A1AA] text-sm">{nextStep.description}</p>
                </motion.div>
              )}
            </motion.div>
          </div>
        ) : (
          /* Conversation Area */
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!activeMode ? (
            <div className="h-full flex items-center justify-center text-center">
              <div>
                <Sparkles className="text-[#27272A] mx-auto mb-4" size={48} />
                <p className="text-[#52525B]">{t('aiCoach.selectAction')}</p>
              </div>
            </div>
          ) : (
            <>
              {/* Initial guidance message */}
              {conversation.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#1C1C1C]/50 p-4 rounded"
                >
                  <p className="text-[#A1A1AA] text-sm mb-3">
                    {activeMode === 'regenerate' && t('aiCoach.guidance.regenerate')}
                    {activeMode === 'adjust' && t('aiCoach.guidance.adjust')}
                    {activeMode === 'question' && t('aiCoach.guidance.question')}
                  </p>
                  
                  {/* Quick prompts */}
                  <p className="text-[#52525B] text-xs mb-2">{t('aiCoach.quickSuggestions')}</p>
                  <div className="flex flex-wrap gap-2">
                    {(suggestedPrompts.length > 0 ? suggestedPrompts : defaultPrompts[activeMode]).map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => handleQuickPrompt(prompt)}
                        className="text-xs px-3 py-1.5 border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Conversation messages */}
              {conversation.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] p-4 ${
                    msg.role === 'user' 
                      ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/20' 
                      : 'bg-[#1C1C1C]'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-2">
                        <Bot size={14} className="text-[#D4AF37]" />
                        <span className="text-[#D4AF37] text-xs font-accent">VISION LAB AI</span>
                      </div>
                    )}
                    <p className="text-[#E4E4E7] text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </motion.div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-[#1C1C1C] p-4 flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-[#D4AF37]" />
                    <span className="text-[#A1A1AA] text-sm">{t('aiCoach.thinking')}</span>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        )}

        {/* Continue Journey Button - Shown on completion screen */}
        {showCompletionScreen && (
          <div className="p-4 border-t border-[#1C1C1C] bg-[#0A0A0A]">
            <Button
              onClick={handleContinueJourney}
              disabled={loadingNextStep}
              className="w-full btn-gold py-4 flex items-center justify-center gap-3"
              data-testid="continue-journey-btn"
            >
              {loadingNextStep ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span className="font-medium">{t('common.loading', 'Chargement...')}</span>
                </>
              ) : (
                <>
                  <span className="font-medium text-base">
                    {t('stepCompletion.continueJourney', 'CONTINUER MON PARCOURS')}
                  </span>
                  <ArrowRight size={20} />
                </>
              )}
            </Button>
            
            {/* Option to make more adjustments */}
            <button
              onClick={() => setShowCompletionScreen(false)}
              className="w-full text-center text-[#52525B] text-sm mt-3 hover:text-[#A1A1AA] transition-colors"
            >
              {t('stepCompletion.moreAdjustments', 'Faire d\'autres ajustements')}
            </button>
          </div>
        )}

        {/* Input Area - Hidden when completion screen is shown */}
        {activeMode && !showCompletionScreen && (
          <div className="p-4 border-t border-[#1C1C1C] bg-[#0A0A0A]">
            {/* Custom adjustment label for adjust mode */}
            {activeMode === 'adjust' && (
              <p className="text-[#D4AF37] text-xs mb-2 font-accent tracking-wider">
                {t('aiCoach.personalAdjustment')}
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-3">
              <Textarea
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                placeholder={t(`aiCoach.placeholder.${activeMode === 'question' ? 'question' : activeMode}`)}
                className="w-full bg-[#1C1C1C] border border-[#27272A] focus:border-[#D4AF37] rounded-none p-3 min-h-[80px] max-h-[120px] text-white placeholder:text-[#52525B] text-sm resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                data-testid="refinement-input"
              />
              <Button
                type="submit"
                disabled={loading || !userMessage.trim()}
                className="w-full btn-gold py-3 flex items-center justify-center gap-2"
                data-testid="refinement-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span className="text-sm font-medium">{t('aiCoach.thinking')}</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span className="text-sm font-medium">{t('aiCoach.apply')}</span>
                  </>
                )}
              </Button>
            </form>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default AIRefinementPanel;
